import cache from '../cache/cache.js';
import { EPSILON } from '../constants/index.js';
import getSpacingInNormalDirection from './getSpacingInNormalDirection.js';
import getVoxelThicknessAlongNormal from './voxelSlab/getVoxelThicknessAlongNormal.js';
import { getConfiguration } from '../init.js';
import { getVolumeLoaderSchemes } from '../loaders/volumeLoader.js';
import { getVolumeId } from './getVolumeId.js';
import { coreLog } from './logger.js';
export const sliceStepLog = coreLog.getLogger('utilities', 'getTargetVolumeAndSpacingInNormalDir');
let lastSliceStepLine = '';
const EPSILON_PART = 1 + EPSILON;
const startsWith = (str, starts) => starts === str.substring(0, Math.min(str.length, starts.length));
const isPrimaryVolume = (volume) => !!getVolumeLoaderSchemes().find((scheme) => startsWith(volume.volumeId, scheme));
export default function getTargetVolumeAndSpacingInNormalDir(viewport, camera, targetId, useSlabThickness = false) {
    const { viewPlaneNormal } = camera;
    const volumeActors = viewport.getActors();
    if (!volumeActors.length) {
        return {
            spacingInNormalDirection: null,
            imageVolume: null,
            actorUID: null,
        };
    }
    const imageVolumes = volumeActors
        .map((va) => {
        const actorUID = va.referencedId ?? va.uid;
        return cache.getVolume(actorUID);
    })
        .filter((iv) => !!iv);
    if (targetId) {
        const targetVolumeId = getVolumeId(targetId);
        const imageVolumeIndex = imageVolumes.findIndex((iv) => targetVolumeId.includes(iv.volumeId));
        const imageVolume = imageVolumes[imageVolumeIndex];
        const { uid: actorUID } = volumeActors[imageVolumeIndex];
        const spacingInNormalDirection = getSpacingInNormal(imageVolume, viewPlaneNormal, viewport, useSlabThickness);
        return { imageVolume, spacingInNormalDirection, actorUID };
    }
    if (!imageVolumes.length) {
        return {
            spacingInNormalDirection: null,
            imageVolume: null,
            actorUID: null,
        };
    }
    const smallest = {
        spacingInNormalDirection: Infinity,
        imageVolume: null,
        actorUID: null,
    };
    const hasPrimaryVolume = imageVolumes.find(isPrimaryVolume);
    for (let i = 0; i < imageVolumes.length; i++) {
        const imageVolume = imageVolumes[i];
        if (hasPrimaryVolume && !isPrimaryVolume(imageVolume)) {
            continue;
        }
        const spacingInNormalDirection = getSpacingInNormal(imageVolume, viewPlaneNormal, viewport);
        if (spacingInNormalDirection * EPSILON_PART <
            smallest.spacingInNormalDirection) {
            smallest.spacingInNormalDirection = spacingInNormalDirection;
            smallest.imageVolume = imageVolume;
            smallest.actorUID = volumeActors[i].uid;
        }
    }
    return smallest;
}
function getSpacingInNormal(imageVolume, viewPlaneNormal, viewport, useSlabThickness = false) {
    const slabThickness = viewport.getProperties?.()?.slabThickness;
    const measure = getConfiguration().rendering?.sliceStepMeasure;
    const geometric = measure === 'l1'
        ? getVoxelThicknessAlongNormal(imageVolume, viewPlaneNormal)
        : getSpacingInNormalDirection(imageVolume, viewPlaneNormal);
    const used = slabThickness && useSlabThickness ? slabThickness : geometric;
    if ((sliceStepLog.getLevel?.() ?? 5) <= 1) {
        const l1 = getVoxelThicknessAlongNormal(imageVolume, viewPlaneNormal);
        const l2 = getSpacingInNormalDirection(imageVolume, viewPlaneNormal);
        if (Math.abs(l1 - l2) > 1e-6 * Math.max(l1, l2)) {
            const line = `slice step [${viewport?.id}] measure=${measure ?? 'l2'} ` +
                `used=${used.toFixed(4)} l1=${l1.toFixed(4)} l2=${l2.toFixed(4)} ` +
                `l1/l2=${(l1 / l2).toFixed(4)} ` +
                `fromSlabThickness=${Boolean(slabThickness && useSlabThickness)}`;
            if (line !== lastSliceStepLine) {
                lastSliceStepLine = line;
                sliceStepLog.debug(line, {
                    slabThickness,
                    useSlabThickness,
                    viewPlaneNormal,
                    spacing: imageVolume.spacing,
                });
            }
        }
    }
    return used;
}
